import { hideBackdrop, showBackdrop } from "./backdrop-utils.js";
import { setBodyLocked } from "./modal-utils.js";

const STORAGE_KEY = "case-reader-theme";
const SHEET_OPEN_CLASS = "is-sheet-open";
const BACKDROP_KEY = "case-reader-sheet";
const LG_MEDIA = "(max-width: 992px)";
const NAV_HIDDEN_CLASS = "is-nav-hidden";
const NAV_SCROLL_DELTA = 6;
const NAV_TOP_THRESHOLD = 48;

function getScrollY(docRef = document) {
  const win = docRef.defaultView;
  const yWin = win ? win.scrollY || win.pageYOffset || 0 : 0;
  const root = docRef.scrollingElement ?? docRef.documentElement;
  const yRoot = root?.scrollTop ?? 0;
  const yBody = docRef.body?.scrollTop ?? 0;
  return Math.max(0, yWin, yRoot, yBody);
}

export function initCaseReader(doc) {
  const root = doc.querySelector(".case-reader");
  if (!root) return;

  initTheme(root);
  initToc(root);
  initMobileSheet(root, doc);
  initNavAutoHide(root, doc);
  initScrollSpy(root);
  initFocusHighlight(root, doc);
}

function initTheme(root) {
  const buttons = root.querySelectorAll(".js-theme-btn");
  if (!buttons.length) return;

  const saved = window.localStorage.getItem(STORAGE_KEY);
  if (saved) applyTheme(root, buttons, saved);

  buttons.forEach((btn) => {
    btn.addEventListener("click", () => {
      const theme = btn.dataset.theme;
      applyTheme(root, buttons, theme);
      window.localStorage.setItem(STORAGE_KEY, theme);
    });
  });
}

function applyTheme(root, buttons, theme) {
  root.dataset.theme = theme;
  buttons.forEach((btn) => {
    btn.classList.toggle("case-reader__theme-btn--active", btn.dataset.theme === theme);
  });
}

function initToc(root) {
  const doc = root.ownerDocument;
  const toc = root.querySelector(".case-reader__toc");
  const toggle = root.querySelector(".js-toc-toggle");
  const collapse = root.querySelector(".js-toc-collapse");
  if (!toc || !toggle) return;

  toggle.addEventListener("click", () => toc.classList.toggle("is-open"));

  if (collapse) {
    collapse.addEventListener("click", () => toc.classList.remove("is-open"));
  }

  const links = root.querySelectorAll(".case-reader__toc-link");
  links.forEach((link) => {
    link.addEventListener("click", (e) => {
      e.preventDefault();
      const id = link.getAttribute("href")?.slice(1);
      const target = id && doc.getElementById(id);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
  });
}

function initNavAutoHide(root, doc) {
  const view = doc.defaultView;
  if (!view) return;

  const mq = view.matchMedia(LG_MEDIA);
  let lastY = getScrollY(doc);
  let ticking = false;

  const setNavHidden = (hidden) => {
    root.classList.toggle(NAV_HIDDEN_CLASS, hidden);
  };

  const onScrollFrame = () => {
    ticking = false;
    if (!mq.matches) return;
    if (root.classList.contains(SHEET_OPEN_CLASS)) return;

    const y = getScrollY(doc);
    if (y < NAV_TOP_THRESHOLD) {
      setNavHidden(false);
      lastY = y;
      return;
    }

    const delta = y - lastY;
    if (delta > NAV_SCROLL_DELTA) {
      setNavHidden(true);
    } else if (delta < -NAV_SCROLL_DELTA) {
      setNavHidden(false);
    }
    lastY = y;
  };

  const onScroll = () => {
    if (!mq.matches) return;
    if (!ticking) {
      ticking = true;
      view.requestAnimationFrame(onScrollFrame);
    }
  };

  const onScrollWin = () => onScroll();
  doc.defaultView?.addEventListener("scroll", onScrollWin, { passive: true });
  const navScrollRoot = doc.scrollingElement ?? doc.documentElement;
  if (navScrollRoot) {
    navScrollRoot.addEventListener("scroll", onScrollWin, { passive: true });
  }

  const onMqChange = (event) => {
    if (!event.matches) {
      setNavHidden(false);
    }
    lastY = getScrollY(doc);
  };

  if (typeof mq.addEventListener === "function") {
    mq.addEventListener("change", onMqChange);
  } else {
    mq.addListener(onMqChange);
  }
}

function initMobileSheet(root, doc) {
  const view = doc.defaultView;
  const burger = root.querySelector(".case-reader__burger");
  const sheet = root.querySelector(".case-reader__sheet");
  const closeBtn = root.querySelector(".case-reader__sheet-close");
  if (!burger || !sheet || !view) return;

  const mq = view.matchMedia(LG_MEDIA);

  const syncSheetAria = () => {
    if (!mq.matches) {
      sheet.removeAttribute("aria-hidden");
      return;
    }
    sheet.setAttribute("aria-hidden", root.classList.contains(SHEET_OPEN_CLASS) ? "false" : "true");
  };

  const setSheetOpen = (open) => {
    root.classList.toggle(SHEET_OPEN_CLASS, open);
    burger.setAttribute("aria-expanded", open ? "true" : "false");
    syncSheetAria();
    if (open) {
      root.classList.remove(NAV_HIDDEN_CLASS);
      setBodyLocked(doc, true);
      showBackdrop(BACKDROP_KEY, doc);
    } else {
      setBodyLocked(doc, false);
      hideBackdrop(BACKDROP_KEY, doc);
    }
  };

  const closeSheet = () => {
    if (!root.classList.contains(SHEET_OPEN_CLASS)) return;
    setSheetOpen(false);
    burger.focus();
  };

  const openSheet = () => {
    if (root.classList.contains(SHEET_OPEN_CLASS)) return;
    setSheetOpen(true);
  };

  const toggleSheet = () => {
    if (root.classList.contains(SHEET_OPEN_CLASS)) closeSheet();
    else openSheet();
  };

  burger.addEventListener("click", () => {
    if (!mq.matches) return;
    toggleSheet();
  });

  closeBtn?.addEventListener("click", () => closeSheet());

  root.querySelectorAll(".case-reader__toc-link").forEach((link) => {
    link.addEventListener("click", () => {
      if (mq.matches) closeSheet();
    });
  });

  doc.addEventListener("click", (event) => {
    if (!event.target.classList.contains("backdrop")) return;
    if (root.classList.contains(SHEET_OPEN_CLASS)) closeSheet();
  });

  doc.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    if (root.classList.contains(SHEET_OPEN_CLASS)) {
      event.preventDefault();
      closeSheet();
    }
  });

  const onViewportChange = () => {
    if (!mq.matches && root.classList.contains(SHEET_OPEN_CLASS)) {
      root.classList.remove(SHEET_OPEN_CLASS);
      burger.setAttribute("aria-expanded", "false");
      setBodyLocked(doc, false);
      hideBackdrop(BACKDROP_KEY, doc);
    }
    syncSheetAria();
  };

  if (typeof mq.addEventListener === "function") {
    mq.addEventListener("change", onViewportChange);
  } else {
    mq.addListener(onViewportChange);
  }

  syncSheetAria();
}

function initScrollSpy(root) {
  const chapters = root.querySelectorAll(".case-reader__chapter");
  const links = root.querySelectorAll(".case-reader__toc-link");
  if (!chapters.length || !links.length) return;

  const linkMap = new Map();
  links.forEach((link) => {
    const id = link.getAttribute("href")?.slice(1);
    if (id) linkMap.set(id, link);
  });

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const link = linkMap.get(entry.target.id);
        if (!link) return;

        if (entry.isIntersecting) {
          links.forEach((l) => l.classList.remove("is-active"));
          link.classList.add("is-active");
        }
      });
    },
    { rootMargin: "-20% 0px -60% 0px" }
  );

  chapters.forEach((ch) => observer.observe(ch));
}

function initFocusHighlight(root, doc) {
  const view = doc.defaultView;
  const content = root.querySelector(".case-reader__content");
  if (!content || !view) return;

  const blocks = Array.from(content.querySelectorAll(".case-reader__text > p"));
  if (!blocks.length) return;

  const textWrap = content.querySelector(".case-reader__text");
  if (!textWrap) return;

  let current = null;
  let currentIndex = -1;
  let rafId = null;

  const indicator = createFocusIndicator(textWrap);
  const indicatorDefaultTransition = indicator ? indicator.style.transition : "";

  function hideIndicator() {
    if (!indicator) return;
    indicator.style.opacity = "0";
  }

  function hasNextParagraphSibling(p) {
    if (!p) return false;
    const next = p.nextElementSibling;
    return !!next && next.tagName === "P";
  }

  function positionIndicator(block, { immediate = false } = {}) {
    if (!indicator || !textWrap || !block) return;
    if (!hasNextParagraphSibling(block)) {
      hideIndicator();
      return;
    }
    if (immediate) {
      indicator.style.transition = "none";
    }
    const wrapRect = textWrap.getBoundingClientRect();
    const blockRect = block.getBoundingClientRect();
    const top = blockRect.top - wrapRect.top + textWrap.scrollTop;
    indicator.style.transform = `translateY(${Math.max(0, top)}px)`;
    indicator.style.height = `${Math.max(0, blockRect.height)}px`;
    indicator.style.opacity = "1";
    if (immediate) {
      indicator.getBoundingClientRect();
      indicator.style.transition = indicatorDefaultTransition;
    }
  }

  function clearFocus() {
    if (current) current.classList.remove("is-focused");
    current = null;
    currentIndex = -1;
    hideIndicator();
  }

  function setFocusedByIndex(nextIndex) {
    if (nextIndex < 0 || nextIndex >= blocks.length) return;
    if (nextIndex === currentIndex) return;

    const next = blocks[nextIndex];
    const hadNoFocus = !current;
    if (current) current.classList.remove("is-focused");
    next.classList.add("is-focused");
    current = next;
    currentIndex = nextIndex;
    positionIndicator(next, { immediate: hadNoFocus });
  }

  function update() {
    rafId = null;
    const contentRect = textWrap.getBoundingClientRect();
    const fitsViewport =
      contentRect.height <= view.innerHeight &&
      contentRect.top >= 0 &&
      contentRect.bottom <= view.innerHeight;

    if (fitsViewport) {
      clearFocus();
      return;
    }

    const viewH = view.innerHeight;
    const center = viewH / 2;
    let closestIndex = -1;
    let minDist = Infinity;

    for (let i = 0; i < blocks.length; i++) {
      const block = blocks[i];
      const rect = block.getBoundingClientRect();
      if (rect.bottom < 0 || rect.top > viewH) continue;

      const blockCenter = rect.top + rect.height / 2;
      const dist = Math.abs(blockCenter - center);

      if (dist < minDist) {
        minDist = dist;
        closestIndex = i;
      }
    }

    if (closestIndex === -1 || minDist > viewH * 0.45) {
      clearFocus();
      return;
    }

    setFocusedByIndex(closestIndex);
  }

  function onScroll() {
    if (!rafId) rafId = view.requestAnimationFrame(update);
  }

  const onScrollWin = () => onScroll();
  doc.defaultView?.addEventListener("scroll", onScrollWin, { passive: true });
  const focusScrollRoot = doc.scrollingElement ?? doc.documentElement;
  if (focusScrollRoot) {
    focusScrollRoot.addEventListener("scroll", onScrollWin, { passive: true });
  }

  view.addEventListener(
    "resize",
    () => {
      if (current) positionIndicator(current, { immediate: true });
    },
    { passive: true }
  );
  update();
}

function createFocusIndicator(textWrap) {
  const el = textWrap.ownerDocument.createElement("div");
  el.className = "case-reader__focus-indicator";
  el.setAttribute("aria-hidden", "true");
  textWrap.appendChild(el);
  return el;
}
